import React, { useEffect, useState } from 'react'
import {
	Box,
	Button,
	MenuItem,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material'
import { AppButtonIcon, AppCardTitle, AppResultsSkeleton, AppNote } from '@components'
import { dsColorNew } from '@palette'
import { MbPage } from 'Pages/MyBusiness/_shared/Ui'

import { AdminProgress, adminCardSx, adminFieldsSx, rowDeleteButtonSx, rowIconButtonSx, rowToneSx } from '../_shared/ui'

/**
 * Module 4 — Pricelist Groups admin.
 *
 * Same shape as Extras Buckets but simpler: each pricelist_code belongs
 * to at most one group. Auto-map applies default keyword rules
 * (Prepaid → Prepaid, Cartrawler|Discover|... → Broker, OFICINAS|WEB-BASIC → Direct).
 */
const API = '/api/my-business/admin'

export default function PricelistGroupsPage({ embedded = false }) {
	const [groups, setGroups] = useState([])
	const [codes,  setCodes]  = useState([])
	const [loading, setLoading] = useState(true)
	const [error,   setError]   = useState(null)
	const [saving,  setSaving]  = useState(false)

	const [newCode, setNewCode] = useState('')
	const [newName, setNewName] = useState('')

	const reload = () => {
		setLoading(true); setError(null)
		Promise.all([
			fetch(`${API}/pricelist-groups`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
			fetch(`${API}/pricelist-code-mappings`, { credentials: 'include' })
				.then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))),
		]).then(([g, c]) => { setGroups(g); setCodes(c) })
		  .catch(e => setError(e.message))
		  .finally(() => setLoading(false))
	}

	useEffect(() => { reload() }, [])

	const call = (path, opts = {}) => {
		setSaving(true); setError(null)
		return fetch(`${API}${path}`, {
			credentials: 'include',
			headers: opts.body ? { 'Content-Type': 'application/json' } : undefined,
			...opts,
		}).then(r => r.json().then(body => ({ ok: r.ok, status: r.status, body })))
		  .then(({ ok, status, body }) => {
			  if (!ok) throw new Error(body?.error || `HTTP ${status}`)
			  return body
		  })
		  .catch(e => { setError(e.message); throw e })
		  .finally(() => setSaving(false))
	}

	const createGroup = () => {
		const code = newCode.trim(); const name = newName.trim()
		if (!code || !name) return
		call('/pricelist-groups',
		     { method: 'POST', body: JSON.stringify({ code, name }) })
			.then(() => { setNewCode(''); setNewName(''); reload() }).catch(() => {})
	}

	const patchGroup = (id, patch) =>
		call(`/pricelist-groups/${id}`,
		     { method: 'PATCH', body: JSON.stringify(patch) })
			.then(reload).catch(() => {})

	const deleteGroup = (id) => {
		if (!window.confirm('Delete this group? Any mapping must be cleared first.')) return
		call(`/pricelist-groups/${id}`, { method: 'DELETE' })
			.then(reload).catch(() => {})
	}

	const setMapping = (pricelist_code, group_id) =>
		call(`/pricelist-code-mappings/${encodeURIComponent(pricelist_code)}`,
		     { method: 'PUT', body: JSON.stringify({ group_id: group_id || null }) })
			.then(reload).catch(() => {})

	const automap = (overwrite = false) =>
		call('/pricelist-code-mappings/automap',
		     { method: 'POST', body: JSON.stringify({ overwrite }) })
			.then(reload).catch(() => {})

	const unmappedCount = codes.filter(c => !c.group_id).length

	const body = (
		<Box>
			<AdminProgress active={saving}/>
			{error && <AppNote severity="error" sx={{ mb: 2 }}>{error}</AppNote>}
			{loading && !groups.length ? <AppResultsSkeleton columns={4}/> : <>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx }}>
					<AppCardTitle icon="layer-group" title="Groups"/>
					<TableContainer sx={{ mt: 1.5 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell width={100}>Order</TableCell>
									<TableCell width={200}>Code</TableCell>
									<TableCell>Name</TableCell>
									<TableCell width={80}/>
								</TableRow>
							</TableHead>
							<TableBody>
								{groups.map(g => (
									<TableRow key={g.id}>
										<TableCell>
											<TextField type="number" size="small" defaultValue={g.sort_order}
											           onBlur={(e) => {
												           const v = Number(e.target.value)
												           if (v !== g.sort_order) patchGroup(g.id, { sort_order: v })
											           }}
											           inputProps={{ style: { width: 60 } }}/>
										</TableCell>
										<TableCell><code>{g.code}</code></TableCell>
										<TableCell>
											<TextField size="small" fullWidth defaultValue={g.name}
											           onBlur={(e) => {
												           const v = e.target.value.trim()
												           if (v && v !== g.name) patchGroup(g.id, { name: v })
											           }}/>
										</TableCell>
										<TableCell>
											<AppButtonIcon icon="trash" size="small" sx={rowDeleteButtonSx}
											               onClick={() => deleteGroup(g.id)}/>
										</TableCell>
									</TableRow>
								))}
								<TableRow>
									<TableCell/>
									<TableCell>
										<TextField size="small" placeholder="code" fullWidth
										           value={newCode}
										           onChange={(e) => setNewCode(e.target.value)}/>
									</TableCell>
									<TableCell>
										<TextField size="small" placeholder="Display name" fullWidth
										           value={newName}
										           onChange={(e) => setNewName(e.target.value)}/>
									</TableCell>
									<TableCell>
										<AppButtonIcon icon="plus" size="small" sx={rowIconButtonSx}
										               onClick={createGroup}
										               disabled={!newCode.trim() || !newName.trim()}/>
									</TableCell>
								</TableRow>
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>

				<Paper sx={{ ...adminCardSx, ...adminFieldsSx, mb: 0 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between"
					       sx={{ mb: 2 }}>
						<AppCardTitle icon="tags" title={`Pricelist codes · ${unmappedCount} unassigned`}/>
						<Stack direction="row" gap={1}>
							<Button size="small" variant="outlined" onClick={() => automap(false)}>
								Auto-map unassigned
							</Button>
							<Button size="small" variant="text" onClick={() => automap(true)}>
								Re-apply defaults
							</Button>
						</Stack>
					</Stack>
					<TableContainer sx={{ maxHeight: 500 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell width={180}>Code</TableCell>
									<TableCell>Name</TableCell>
									<TableCell align="right" width={100}>Bookings</TableCell>
									<TableCell width={260}>Group</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{codes.map(c => (
									<TableRow key={c.pricelist_code}
									          sx={rowToneSx({ unassigned: !c.group_id })}>
										<TableCell><code>{c.pricelist_code}</code></TableCell>
										<TableCell>
											{c.pricelist_name || (
												<Typography component="span"
												            sx={{ fontSize: 12.5, fontStyle: 'italic', color: dsColorNew.textSubtle }}>
													— no en Ranteon —
												</Typography>
											)}
										</TableCell>
										<TableCell align="right">
											{new Intl.NumberFormat('es-ES').format(c.n_bookings)}
										</TableCell>
										<TableCell>
											<Select size="small" fullWidth
											        value={c.group_id || ''}
											        onChange={(e) => setMapping(c.pricelist_code, e.target.value || null)}
											        displayEmpty>
												<MenuItem value=""><em>Unassigned</em></MenuItem>
												{groups.map(g => (
													<MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
												))}
											</Select>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Paper>
			</>}
		</Box>
	)

	if (embedded) return body
	return (
		<MbPage title="Pricelist Groups" subtitle="Group pricelist codes into Direct/Broker/Prepaid/…">
			{body}
		</MbPage>
	)
}
